"use client";

import { BrandLockup } from "@/components/brand";
import { ImpromptuSettings } from "@/components/impromptu/ImpromptuSettings";
import { PromptStudio } from "@/components/impromptu/PromptStudio";
import { TopicLibrary } from "@/components/impromptu/TopicLibrary";
import { LandingFooter } from "@/components/landing/LandingFooter";
import type {
  ImpromptuLanguageCode,
  ImpromptuLearnerProfile,
  ImpromptuPrompt,
  ImpromptuPromptSelection,
} from "@/lib/impromptu/config";
import { IMPROMPTU_PROMPT_CATEGORIES } from "@/lib/impromptu/config";
import { IMPROMPTU_COPY } from "@/lib/impromptu/copy";
import {
  IMPROMPTU_LANGUAGE_PAGES,
  type ImpromptuLanguagePage,
} from "@/lib/impromptu/language-pages";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import Link from "next/link";

/**
 * The public marketing surface at `/impromptu`.
 *
 * Three things about the shape of this page are deliberate.
 *
 * Every string comes from `IMPROMPTU_COPY.landing`. The page previously held a
 * local `FEEDBACK_ROWS` const whose four rows were worded differently from the
 * four in the copy module — two wordings of one table, which is the exact drift
 * centralising the copy was meant to end.
 *
 * The colour sequence runs warm-light through the hero, the library and the
 * feedback proof, then transitions once into the flat near-black closing region
 * and footer. That single transition is what `docs/design/marketing-and-acquisition.md`
 * asks for; the old page instead put a near-black stats band in the middle of the
 * warm sequence and then returned to white, which reads as three unrelated pages.
 *
 * It does not adopt the shared `LandingNavbar`. That component is hardwired to the
 * main acquisition nav (Features, AI Tutors, Practice, Tools, Pricing, FAQ) and to
 * `#demo`, so every link in it would send someone who arrived here off this page,
 * and the in-page `#topic-library` and `#feedback` anchors would have nowhere to
 * live. `LandingFooter variant="dark"` is shared, because it pairs with the
 * near-black closing region and has no such coupling.
 */

const COPY = IMPROMPTU_COPY.landing;

/** Light-surface focus treatment, matching `LandingNavbar`. */
const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F5F5F0]";

type ImpromptuLandingProps = {
  languageCode: ImpromptuLanguageCode;
  learnerProfile: ImpromptuLearnerProfile;
  prompt: ImpromptuPromptSelection;
  prompts: ReadonlyArray<ImpromptuPrompt>;
  onLanguageChange: (language: ImpromptuLanguageCode) => void;
  onLearnerProfileChange: (profile: ImpromptuLearnerProfile) => void;
  onPromptChange: (prompt: ImpromptuPromptSelection) => void;
  onStart: () => void;
  languagePage?: ImpromptuLanguagePage;
};

/**
 * Both in-page jumps go through here so neither one can forget the motion
 * preference. `matchMedia` is read at click time rather than captured in a hook
 * because the setting can change while the page is open.
 */
function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    block: "start",
  });
}

export function ImpromptuLanding({
  languageCode,
  learnerProfile,
  prompt,
  prompts,
  onLanguageChange,
  onLearnerProfileChange,
  onPromptChange,
  onStart,
  languagePage,
}: ImpromptuLandingProps) {
  const reduceMotion = useReducedMotion();
  const scenarios = languagePage?.scenarios ?? COPY.scenarios;
  const faqs = languagePage ? [...languagePage.faqs, ...COPY.faqs] : COPY.faqs;
  const featuredPrompts = languagePage
    ? (["opinion", "story", "work", "everyday"] as const)
        .map((category) => prompts.find((candidate) => candidate.category === category))
        .filter((candidate): candidate is ImpromptuPrompt => Boolean(candidate))
    : [];
  const categoryLabels = Object.fromEntries(
    IMPROMPTU_PROMPT_CATEGORIES.map((category) => [category.value, category.label])
  ) as Record<ImpromptuPrompt["category"], string>;

  return (
    <div className="min-h-[100dvh] bg-[#F5F5F0] text-[#1C1C1E]">
      <header className="sticky top-0 z-40 border-b border-[#E5E5EA] bg-[#F5F5F0]/95 backdrop-blur-md">
        <div className="mx-auto flex min-h-20 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
          <Link href="/" aria-label={COPY.homeLabel} className={`shrink-0 rounded-lg ${FOCUS}`}>
            <BrandLockup surface="light" className="h-auto w-[132px] sm:w-[168px]" priority />
          </Link>
          <nav className="hidden items-center gap-7 text-sm font-bold text-[#2C2C2E] lg:flex">
            <a
              href="#how-it-works"
              className={`rounded-lg transition-colors hover:text-[#A8431F] ${FOCUS}`}
            >
              {COPY.navHowItWorks}
            </a>
            <a
              href="#topic-library"
              className={`rounded-lg transition-colors hover:text-[#A8431F] ${FOCUS}`}
            >
              {COPY.navTopics}
            </a>
            <a
              href="#feedback"
              className={`rounded-lg transition-colors hover:text-[#A8431F] ${FOCUS}`}
            >
              {COPY.navFeedback}
            </a>
            <a href="#faq" className={`rounded-lg transition-colors hover:text-[#A8431F] ${FOCUS}`}>
              {COPY.navFaq}
            </a>
          </nav>
          <button
            type="button"
            onClick={() => scrollTo("impromptu-studio")}
            className={`inline-flex min-h-11 shrink-0 items-center rounded-full border-2 border-[#1C1C1E] px-5 text-sm font-bold text-[#1C1C1E] transition-colors hover:bg-[#1C1C1E] hover:text-white ${FOCUS}`}
          >
            {COPY.startPractice}
          </button>
        </div>
      </header>

      <main>
        <section className="bg-[#F5F5F0]">
          <div className="mx-auto max-w-7xl px-5 pb-16 pt-10 sm:px-8 md:pb-24 md:pt-14">
            <div className="grid gap-8 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-12">
              <div className="min-w-0 lg:sticky lg:top-28">
                {languagePage ? (
                  <nav
                    aria-label="Breadcrumb"
                    className="mb-6 flex flex-wrap items-center gap-2 text-sm text-[#636366]"
                  >
                    <Link href="/impromptu" className={`rounded hover:text-[#A8431F] ${FOCUS}`}>
                      Impromptu practice
                    </Link>
                    <span aria-hidden="true">/</span>
                    <span aria-current="page">{languagePage.shortName}</span>
                  </nav>
                ) : null}
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
                  {languagePage?.eyebrow ?? COPY.eyebrow}
                </p>
                <h1 className="mt-5 max-w-[13ch] text-[2.6rem] font-extrabold leading-[1.02] text-[#1C1C1E] sm:text-[3.4rem] lg:text-[4rem]">
                  {languagePage?.heading ?? COPY.heading}
                </h1>
                <p className="mt-6 max-w-[44ch] text-lg leading-[1.6] text-[#2C2C2E]">
                  {languagePage?.lead ?? COPY.subheading}
                </p>
                <p className="mt-4 text-sm font-bold text-[#A8431F]">
                  {languagePage
                    ? `32 ${languagePage.shortName} topics. One useful second take.`
                    : COPY.languageLine}
                </p>
                <ImpromptuSettings
                  className="mt-8"
                  languageCode={languageCode}
                  learnerProfile={learnerProfile}
                  onLanguageChange={onLanguageChange}
                  onLearnerProfileChange={onLearnerProfileChange}
                />
                <p className="mt-6 max-w-[40ch] text-sm leading-6 text-[#8E8E93]">
                  {IMPROMPTU_COPY.privacy}
                </p>
              </div>

              <motion.div
                id="impromptu-studio"
                className="min-w-0 scroll-mt-24"
                initial={reduceMotion ? undefined : { opacity: 0, y: 16 }}
                animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <PromptStudio
                  surface="landing"
                  prompt={prompt}
                  prompts={prompts}
                  languageCode={languageCode}
                  learnerProfile={learnerProfile}
                  onPromptChange={onPromptChange}
                  onStart={onStart}
                  onBrowsePrompts={() => scrollTo("topic-library")}
                />
              </motion.div>
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className="scroll-mt-24 bg-white py-16 md:py-24"
          aria-labelledby="impromptu-story-title"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
                  {COPY.storyEyebrow}
                </p>
                <h2
                  id="impromptu-story-title"
                  className="mt-4 max-w-[15ch] text-[2.25rem] font-extrabold leading-[1.08] text-[#1C1C1E] sm:text-[3rem]"
                >
                  {languagePage?.storyHeading ?? COPY.storyHeading}
                </h2>
                <p className="mt-6 max-w-[46ch] text-lg leading-[1.65] text-[#2C2C2E]">
                  {languagePage?.storyBody ?? COPY.storyBody}
                </p>
              </div>
              <div className="grid border-t border-[#E5E5EA] sm:grid-cols-2">
                {scenarios.map(([title, description], index) => (
                  <div
                    key={title}
                    className={`border-b border-[#E5E5EA] py-6 sm:px-6 ${
                      index % 2 === 0 ? "sm:border-r" : ""
                    }`}
                  >
                    <p className="text-xs font-bold tabular-nums text-[#A8431F]">
                      {String(index + 1).padStart(2, "0")}
                    </p>
                    <h3 className="mt-4 text-lg font-bold text-[#1C1C1E]">{title}</h3>
                    <p className="mt-2 max-w-[30ch] text-sm leading-6 text-[#2C2C2E]">
                      {description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {languagePage ? (
          <section
            className="bg-white py-16 md:py-24"
            aria-labelledby="impromptu-language-guide-title"
          >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <div className="grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
                    A speaking drill for language learners
                  </p>
                  <h2
                    id="impromptu-language-guide-title"
                    className="mt-4 max-w-[18ch] text-[2.25rem] font-extrabold leading-[1.08] sm:text-[3rem]"
                  >
                    What is impromptu {languagePage.shortName} speaking practice?
                  </h2>
                  <p className="mt-6 max-w-[48ch] text-lg leading-[1.65] text-[#2C2C2E]">
                    It is the practice of answering an unfamiliar question in {languagePage.name}{" "}
                    without writing the response first. The point is not to give a polished speech.
                    It is to notice which words and structures are available when you have to create
                    the answer yourself.
                  </p>
                  <ol className="mt-8 space-y-5 border-t border-[#E5E5EA] pt-6">
                    {[
                      [
                        "Choose a question",
                        "Browse the library, spin a topic, generate one or write your own.",
                      ],
                      [
                        "Answer in one take",
                        "Start when you are ready and speak for up to 90 seconds.",
                      ],
                      [
                        "Use the report",
                        "Read the transcript, choose one change and record a stronger second take.",
                      ],
                    ].map(([title, body], index) => (
                      <li key={title} className="grid grid-cols-[2rem_1fr] gap-3">
                        <span className="text-xs font-bold tabular-nums text-[#A8431F]">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <h3 className="font-bold">{title}</h3>
                          <p className="mt-1 text-sm leading-6 text-[#2C2C2E]">{body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>

                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
                    Four {languagePage.shortName} topics to try
                  </p>
                  <div className="mt-4 border-t border-[#D1D1D6]">
                    {featuredPrompts.map((featuredPrompt) => (
                      <button
                        key={featuredPrompt.id}
                        type="button"
                        onClick={() => {
                          onPromptChange({ ...featuredPrompt, source: "curated" });
                          scrollTo("impromptu-studio");
                        }}
                        className={`group grid w-full gap-2 border-b border-[#D1D1D6] py-5 text-left sm:grid-cols-[8rem_1fr_auto] sm:items-center sm:gap-5 ${FOCUS}`}
                      >
                        <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#A8431F]">
                          {categoryLabels[featuredPrompt.category]}
                        </span>
                        <span
                          lang={languagePage.code}
                          className="font-bold leading-6 text-[#1C1C1E]"
                        >
                          {featuredPrompt.text}
                        </span>
                        <span
                          aria-hidden="true"
                          className="text-lg text-[#A8431F] transition-transform group-hover:translate-x-1"
                        >
                          →
                        </span>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => scrollTo("topic-library")}
                    className={`mt-6 inline-flex min-h-11 items-center rounded-xl border border-[#1C1C1E] px-5 text-sm font-bold transition-colors hover:bg-[#1C1C1E] hover:text-white ${FOCUS}`}
                  >
                    Browse all 32 {languagePage.shortName} topics
                  </button>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {languagePage ? (
          <section
            className="bg-[#F5F5F0] py-16 md:py-24"
            aria-labelledby="impromptu-language-focus-title"
          >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-16">
                <div>
                  <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
                    Speaking {languagePage.shortName} under pressure
                  </p>
                  <h2
                    id="impromptu-language-focus-title"
                    className="mt-4 max-w-[17ch] text-[2.25rem] font-extrabold leading-[1.08] sm:text-[3rem]"
                  >
                    {languagePage.focusHeading}
                  </h2>
                  <p className="mt-6 max-w-[45ch] text-lg leading-[1.65] text-[#2C2C2E]">
                    {languagePage.focusLead}
                  </p>
                </div>
                <div className="border-t border-[#D1D1D6]">
                  {languagePage.focusPoints.map(([title, description], index) => (
                    <div
                      key={title}
                      className="grid gap-2 border-b border-[#D1D1D6] py-6 sm:grid-cols-[3rem_12rem_1fr] sm:gap-5"
                    >
                      <span className="text-xs font-bold tabular-nums text-[#A8431F]">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <h3 className="font-bold">{title}</h3>
                      <p className="max-w-[48ch] text-sm leading-6 text-[#2C2C2E]">{description}</p>
                    </div>
                  ))}
                  <aside className="mt-8 border-l-4 border-[#FD954F] bg-white p-6">
                    <h3 className="font-bold">What the level setting changes</h3>
                    <p className="mt-2 max-w-[58ch] text-sm leading-6 text-[#2C2C2E]">
                      The 32 reviewed prompts stay the same. Your level changes how the report
                      judges grammar, vocabulary, structure and the most useful next step. Generated
                      prompts also use the level you selected.
                    </p>
                  </aside>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <section className="bg-[#F5F5F0] py-16 md:py-24" aria-labelledby="impromptu-change-title">
          <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start lg:gap-16">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
                {COPY.transformationEyebrow}
              </p>
              <h2
                id="impromptu-change-title"
                className="mt-4 max-w-[16ch] text-[2.25rem] font-extrabold leading-[1.08] text-[#1C1C1E] sm:text-[3rem]"
              >
                {COPY.transformationHeading}
              </h2>
              <p className="mt-6 max-w-[44ch] text-lg leading-[1.65] text-[#2C2C2E]">
                {COPY.transformationBody}
              </p>
            </div>
            <div className="border border-white/10 bg-[#15161B] p-6 text-white shadow-[0_24px_60px_rgba(28,28,30,0.18)] sm:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#FD954F]">
                {COPY.exampleLabel}
              </p>
              {[
                [COPY.examplePromptLabel, COPY.examplePrompt],
                [COPY.exampleFirstLabel, COPY.exampleFirst],
                [COPY.exampleFeedbackLabel, COPY.exampleFeedback],
                [COPY.exampleSecondLabel, COPY.exampleSecond],
              ].map(([label, value], index) => (
                <div
                  key={label}
                  className={`${index === 0 ? "mt-6" : "mt-5 border-t border-white/10 pt-5"}`}
                >
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8E8E93]">
                    {label}
                  </p>
                  <p
                    className={`mt-2 leading-7 ${index === 2 ? "font-bold text-[#FD954F]" : "text-white"}`}
                  >
                    {value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          id="feedback"
          className="scroll-mt-24 bg-[#F9F9F9] py-16 md:py-24"
          aria-labelledby="impromptu-feedback-title"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-10 lg:grid-cols-2 lg:items-start lg:gap-16">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
                  {COPY.feedbackEyebrow}
                </p>
                <h2
                  id="impromptu-feedback-title"
                  className="mt-4 max-w-[20ch] text-[2rem] font-extrabold leading-[1.18] text-[#1C1C1E] sm:text-[2.75rem]"
                >
                  {COPY.feedbackHeading}
                </h2>
                <p className="mt-5 max-w-[46ch] text-lg leading-[1.6] text-[#2C2C2E]">
                  {COPY.feedbackSubheading}
                </p>
              </div>

              {/* The four sections live in the preview rather than in a list
                  beside it, so the page states them once. */}
              <div className="rounded-2xl border border-white/10 bg-[#15161B] p-6 text-white shadow-[0_24px_60px_rgba(28,28,30,0.22)] sm:p-8">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#FD954F]">
                  {COPY.feedbackPreviewCaption}
                </p>
                <h3 className="mt-3 text-xl font-bold leading-[1.25]">
                  {COPY.feedbackPreviewTitle}
                </h3>
                <dl className="mt-6 space-y-5 border-t border-white/10 pt-6">
                  {COPY.feedbackRows.map(([term, description]) => (
                    <div key={term} className="flex gap-3">
                      <Check
                        className="mt-0.5 h-5 w-5 shrink-0 text-[#FD954F]"
                        aria-hidden="true"
                      />
                      <div>
                        <dt className="text-sm font-bold text-white">{term}</dt>
                        <dd className="mt-1 text-sm leading-6 text-[#AEAEB2]">{description}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        <TopicLibrary prompts={prompts} onSelect={onPromptChange} />

        <section
          id="languages"
          className="scroll-mt-24 bg-white py-14 md:py-18"
          aria-labelledby="impromptu-languages-title"
        >
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <h2
              id="impromptu-languages-title"
              className="text-2xl font-extrabold tracking-[-0.035em] sm:text-3xl"
            >
              Practise impromptu speaking in 14 languages.
            </h2>
            <p className="mt-3 max-w-2xl leading-7 text-[#2C2C2E]">
              Every language has its own 32-topic library and a page built around the speaking
              decisions that language asks you to make.
            </p>
            <nav aria-label="Impromptu practice languages" className="mt-7 flex flex-wrap gap-3">
              {IMPROMPTU_LANGUAGE_PAGES.map((page) => (
                <Link
                  key={page.code}
                  href={page.path}
                  aria-current={languagePage?.code === page.code ? "page" : undefined}
                  className={`inline-flex min-h-11 items-center rounded-full border px-5 text-sm font-bold transition-colors ${FOCUS} ${
                    languagePage?.code === page.code
                      ? "border-[#A8431F] bg-[#A8431F] text-white"
                      : "border-[#D1D1D6] text-[#1C1C1E] hover:border-[#A8431F] hover:text-[#A8431F]"
                  }`}
                >
                  {page.shortName}
                </Link>
              ))}
            </nav>
          </div>
        </section>

        <section
          id="faq"
          className="scroll-mt-24 bg-[#F5F5F0] py-16 md:py-24"
          aria-labelledby="impromptu-faq-title"
        >
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
              {COPY.faqEyebrow}
            </p>
            <h2
              id="impromptu-faq-title"
              className="mt-4 max-w-[19ch] text-[2.25rem] font-extrabold leading-[1.08] text-[#1C1C1E] sm:text-[3rem]"
            >
              {COPY.faqHeading}
            </h2>
            <div className="mt-10 border-t border-[#D1D1D6]">
              {faqs.map(([question, answer]) => (
                <details key={question} className="group border-b border-[#D1D1D6] py-5">
                  <summary
                    className={`cursor-pointer list-none pr-10 text-lg font-bold text-[#1C1C1E] ${FOCUS}`}
                  >
                    {question}
                  </summary>
                  <p className="mt-3 max-w-3xl text-base leading-7 text-[#2C2C2E]">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* The one transition into near-black, which the footer then continues. */}
        <section
          className="bg-[#0F0F14] py-16 text-white md:py-24"
          aria-labelledby="impromptu-closing-title"
        >
          <div className="mx-auto max-w-7xl px-5 text-center sm:px-8">
            <h2
              id="impromptu-closing-title"
              className="mx-auto max-w-[18ch] text-[2rem] font-extrabold leading-[1.18] sm:text-[3rem] sm:leading-[1.08]"
            >
              {COPY.closingHeading}
            </h2>
            <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-[1.6] text-[#AEAEB2]">
              {COPY.closingBody}
            </p>
            <button
              type="button"
              onClick={() => scrollTo("impromptu-studio")}
              className="mt-9 inline-flex min-h-14 items-center gap-2 rounded-xl bg-[#FD954F] px-8 text-base font-bold text-[#1C1C1E] transition-colors hover:bg-[#F28747] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F0F14]"
            >
              {COPY.closingAction}
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </section>
      </main>

      <LandingFooter variant="dark" />
    </div>
  );
}
